import moment from 'moment';

import { DAYS_OF_WEEK } from '@src/constants/commons';
import { translateString } from '@src/util/intl/translateString';

const BASE_URL = 'shifts';

export const PATHS = {
  BASE_URL,
  CREATE_SHIFT: `${BASE_URL}/create`,
  EDIT_SHIFT: `${BASE_URL}/edit/:id`,
  SHIFT_OPTIONS: `${BASE_URL}/options`,
  SHIFT_FIXED: `${BASE_URL}/fixed`,
  SHIFT_PART_TIME: `${BASE_URL}/part-time`,
  SHIFT_WORKDAY: `${BASE_URL}/shiftWork`,
  SHIFT_CYCLE: `${BASE_URL}/cycle`,
  SHIFT_BIWEEKLY: `${BASE_URL}/biweekly`,
} as const;

export const SHIFT_OPTIONS = [
  {
    id: 1,
    title: translateString('shifts.fixed.title'),
    description: translateString('shifts.fixed.description'),
    href: PATHS.SHIFT_FIXED,
  },
  {
    id: 2,
    title: translateString('shifts.part.time.title'),
    description: translateString('shifts.part.time.description'),
    href: PATHS.SHIFT_PART_TIME,
  },
  {
    id: 3,
    title: translateString('shifts.work.title'),
    description: translateString('shifts.work.description'),
    href: PATHS.SHIFT_WORKDAY,
  },
  {
    id: 4,
    title: translateString('shifts.cycle.title'),
    description: translateString('shifts.cycle.description'),
    href: PATHS.SHIFT_CYCLE,
  },
  {
    id: 5,
    title: translateString('shifts.biweekly.title'),
    description: translateString('shifts.biweekly.description'),
    href: PATHS.SHIFT_BIWEEKLY,
  },
];

export const KIND_OF_CYCLES = [
  {
    label: translateString('shifts.select.cycle'),
    value: null,
  },
  {
    label: translateString('shifts.biweekly.2.weeks'),
    value: 2,
  },
  {
    label: translateString('shifts.biweekly.3.weeks'),
    value: 3,
  },
  {
    label: translateString('shifts.biweekly.4.weeks'),
    value: 4,
  },
];

export const SHIFT_TYPES = {
  FIXED: 'fixed',
  CYCLE: 'cycle',
  BIWEEKLY: 'biweek',
  SHIFT_WORK: 'shift',
  PART_TIME: 'partTime',
} as const;

export type ShiftType = (typeof SHIFT_TYPES)[keyof typeof SHIFT_TYPES];

export const SHIFT_TYPES_OPTIONS = {
  ...SHIFT_TYPES,
  EXCEPTIONAL: 'exceptional',
} as const;

export type ShiftTypeOption = (typeof SHIFT_TYPES_OPTIONS)[keyof typeof SHIFT_TYPES_OPTIONS];

export const DEFAULT_ACTIVE_DAY = {
  isActive: true,
  startTime: moment('08:00', 'HH:mm'),
  endTime: moment('08:00', 'HH:mm'),
  breakTime: null as number | null,
  startBreakTime: null as moment.Moment | null,
};

export const COLACION_OPTIONS = [
  { value: null as number | null, label: translateString('shifts.break.none') },
  { value: 30, label: translateString('shifts.break.30.minutes') },
  { value: 45, label: translateString('shifts.break.45.minutes') },
  { value: 60, label: translateString('shifts.break.60.minutes') },
  { value: 90, label: translateString('shifts.break.90.minutes') },
  { value: 120, label: translateString('shifts.break.120.minutes') },
];

export const DEFAULT_SHIFT_TYPE_OPTIONS = [
  { value: SHIFT_TYPES_OPTIONS.FIXED, label: translateString('shifts.type.fixed') },
  { value: SHIFT_TYPES_OPTIONS.BIWEEKLY, label: translateString('shifts.type.biweekly') },
  { value: SHIFT_TYPES_OPTIONS.SHIFT_WORK, label: translateString('shifts.type.shiftWork') },
  { value: SHIFT_TYPES_OPTIONS.CYCLE, label: translateString('shifts.type.cycle') },
  { value: SHIFT_TYPES_OPTIONS.PART_TIME, label: translateString('shifts.type.partTime') },
  { value: SHIFT_TYPES_OPTIONS.EXCEPTIONAL, label: translateString('shifts.type.exceptional') },
];

export const DEFAULT_TIME_OPTIONS = [
  { value: '', label: translateString('shifts.type.all') },
  { value: '06:00', label: '06:00' },
  { value: '09:00', label: '09:00' },
  { value: '14:00', label: '14:00' },
  { value: '18:00', label: '18:00' },
  { value: '22:00', label: '22:00' },
];

export const DEFAULT_COLACION_OPTIONS = [
  { value: -1 as number | null, label: translateString('general.all') },
  ...COLACION_OPTIONS.map(option => ({
    value: option.value,
    label: option.label,
  })),
];

export const DEFAULT_DAYS_OPTIONS = DAYS_OF_WEEK.map(day => ({
  value: day.value,
  label: day.short,
}));

export const PATHS_ACCORDING_TO_TYPE = {
  [SHIFT_TYPES.FIXED]: PATHS.SHIFT_FIXED,
  [SHIFT_TYPES.PART_TIME]: PATHS.SHIFT_PART_TIME,
  [SHIFT_TYPES.SHIFT_WORK]: PATHS.SHIFT_WORKDAY,
  [SHIFT_TYPES.CYCLE]: PATHS.SHIFT_CYCLE,
  [SHIFT_TYPES.BIWEEKLY]: PATHS.SHIFT_BIWEEKLY,
};

export const SHIFT_STATUS = {
  ACTIVE: 'active',
  PENDING: 'pending',
} as const;

export type ShiftStatus = (typeof SHIFT_STATUS)[keyof typeof SHIFT_STATUS];

export const SHIFT_LIST_VIEW_STORAGE_KEY = 'shiftList';
export const SHIFT_LIST_FILTERS_STORAGE_KEY = 'shiftListFilters';
export const SHIFT_LIST_SORT_STORAGE_KEY = 'shiftListSort';
export const SHIFT_LIST_PAGE_SIZE = 10;
export const SHIFT_LIST_SEARCH_IDS_SIZE = 100;

/** Elastic generic-query entity key (differs from Firestore collection `work_shifts`). */
export const SHIFT_ELASTIC_ENTITY = 'work_shift';

export const SHIFT_STATE_FILTER_OPTIONS = [
  'all',
  SHIFT_STATUS.ACTIVE,
  SHIFT_STATUS.PENDING,
] as const;
